import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useIsApiReady } from "../hooks/use-api-ready";
import {
  groupKitExpertSkillControllerAssociateSkillWithExpert,
  groupKitExpertSkillControllerDissociateSkillFromExpert,
  groupKitExpertSkillControllerGetExpertSkills,
} from "../lib/gen-api";
import { queryKeys } from "../lib/query-keys";

export const useExpertSkillsData = (
  groupUuid: string | null,
  kitUuid: string | null,
  expertUuid: string | null,
) => {
  const queryClient = useQueryClient();

  const { data, isPending, isPlaceholderData } = useQuery({
    enabled: useIsApiReady() && !!groupUuid && !!kitUuid && !!expertUuid,
    queryKey: queryKeys.expertSkills(groupUuid!, kitUuid!, expertUuid!),
    queryFn: async () => {
      const response = await groupKitExpertSkillControllerGetExpertSkills({
        path: {
          groupUuid: groupUuid!,
          kitUuid: kitUuid!,
          expertUuid: expertUuid!,
        },
      });
      return response.data?.data || [];
    },
  });
  const skillUuids =
    !expertUuid || isPending || isPlaceholderData ? [] : (data ?? []);

  const { mutateAsync: associateSkill, isPending: isAssociating } = useMutation(
    {
      mutationFn: async ({
        skillUuid,
        kitUuid: kitUuidOverride,
        expertUuid: expertUuidOverride,
      }: {
        skillUuid: string;
        kitUuid?: string;
        expertUuid?: string;
      }) => {
        const resolvedKitUuid = kitUuidOverride ?? kitUuid;
        const resolvedExpertUuid = expertUuidOverride ?? expertUuid;
        if (!groupUuid || !resolvedKitUuid || !resolvedExpertUuid) {
          throw new Error("Required UUIDs missing");
        }

        return groupKitExpertSkillControllerAssociateSkillWithExpert({
          path: {
            groupUuid,
            kitUuid: resolvedKitUuid,
            expertUuid: resolvedExpertUuid,
            skillUuid,
          },
        });
      },
      onSuccess: (
        _data,
        { kitUuid: kitUuidOverride, expertUuid: expertUuidOverride },
      ) => {
        const resolvedKitUuid = kitUuidOverride ?? kitUuid!;
        const resolvedExpertUuid = expertUuidOverride ?? expertUuid!;
        queryClient.invalidateQueries({
          queryKey: queryKeys.expertSkills(
            groupUuid!,
            resolvedKitUuid,
            resolvedExpertUuid,
          ),
        });
        toast.success("Skill linked successfully");
      },
      onError: (error) => {
        console.error("Failed to associate skill:", error);
        toast.error("Failed to link skill");
      },
    },
  );

  const { mutateAsync: dissociateSkill, isPending: isDissociating } =
    useMutation({
      mutationFn: async (skillUuid: string) => {
        if (!groupUuid || !kitUuid || !expertUuid) {
          throw new Error("Required UUIDs missing");
        }

        return groupKitExpertSkillControllerDissociateSkillFromExpert({
          path: {
            groupUuid,
            kitUuid,
            expertUuid,
            skillUuid,
          },
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.expertSkills(groupUuid!, kitUuid!, expertUuid!),
        });
        toast.success("Skill unlinked successfully");
      },
      onError: (error) => {
        console.error("Failed to dissociate skill:", error);
        toast.error("Failed to unlink skill");
      },
    });

  return {
    skillUuids,
    associateSkill,
    dissociateSkill,
    isAssociating,
    isDissociating,
  };
};
